import { isAnyOf } from '@reduxjs/toolkit'
import { UppyFileUploadActions } from '@/uppy-file-upload/store/slice'
import { useTimelineEditingLease } from '../../hooks/useTimelineEditingLease'
import { addTimelineAssetListener } from '../../store/listeners'
import { UserFilesPollingActions } from '@/user_files/polling/store/slice'
import { UserFileProcessingStatus } from '@/user_files/schemas/processing-status'
import { EditingLeaseState } from '@/editing-leases/schemas'
import { refreshMatterAfterWriteAccessError } from '../../utils/refresh-matter-after-write-access-error'
import { FileStagingPage } from '@/matter-asset-common/files/components/FileStagingPage'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useAppDispatch, useAppSelector } from '@/store/store-hooks'
import { RootState } from '@/store/store'
import MatterBreadcrumbsTimelineAsset from '@/matter/components/matter-breadcrumbs/MatterBreadcrumbsTimelineAsset'
import { matterPaths } from '@/routing/paths'
import { useUserFiles } from '@/user_files/files/hooks/useUserFiles'
import { selectAllFiles, selectUploadFilesByServerFileId } from '@/uppy-file-upload/store/selectors'
import TimelineFileCard from './TimelineFileCard'
import { getTimelineAssetFileCardState } from '../../utils/get-timeline-asset-file-card-state'
import { useTimelineAssetFilesUpload } from '../../hooks/useTimelineAssetFilesUpload'
import { postPreviewAddFiles } from '../../fetch/post-preview-add-files'
import { TimelineAssetActions } from '../../store/slice'
import {
  selectInitializePending,
  selectInitializeError,
  selectIsUpdateActive,
  selectFailedFilesUpdate,
  selectTimelineAssetForMatter,
  selectMutationError,
  selectMutationPending,
} from '../../store/selectors'
import { FileAddDisposition, FileAddPreviewItem } from '../../store/schemas'
import { AnalyticsEvent } from '@/analytics/schema/events.schema'
import { trackTimelineEvent } from '@/analytics/utils/timeline-analytics'
import { AddFilesMatterFilesDialog } from './AddFilesMatterFilesDialog'
import { ADD_FILES_MODAL_TAB, type AddFilesModalInitialTab } from '@/chat-common/components/source-dialogs/AddFilesModal'
import { MatterRecordStagedFileCard } from '@/matter-asset-common/files/components/MatterRecordStagedFileCard'
import { TimelineFileStatus } from '../../constants'
import { dispositionDisplay } from './disposition-display'
import { IncludedInTimelineDrawer } from './IncludedInTimelineDrawer'

const kPreviewDebounceMs = 400

/** The staged row shows a spinner and this line while the upload pipeline is still running. */
function inProgressLabel(status?: TimelineFileStatus): string | null {
  if (status === TimelineFileStatus.UPLOADING) return 'Uploading...'
  if (status === TimelineFileStatus.PROCESSING) return 'Processing...'
  return null
}

/**
 * Add Files: stage uploads and matter files, preview their dispositions against the
 * living timeline (net-new extraction only), then generate or update it.
 */
export default function AddFilesPage() {
  const dispatch = useAppDispatch()
  const navigate = useNavigate()
  const { matterId } = useParams<{ matterId: string }>()

  const asset = useAppSelector((state: RootState) => selectTimelineAssetForMatter(state, { matterId }))
  const isUpdateActive = useAppSelector((state: RootState) => selectIsUpdateActive(state, { matterId }))
  const failedFilesUpdate = useAppSelector((state: RootState) => selectFailedFilesUpdate(state, { matterId }))
  const staging = useAppSelector((state: RootState) => state.timelineAssetState.addFilesStaging)
  const uploadFilesByClientTmpId = useAppSelector(selectAllFiles)
  const uploadFilesByServerFileId = useAppSelector(selectUploadFilesByServerFileId)

  const initializePending = useAppSelector((state: RootState) => selectInitializePending(state, { matterId }))
  const mutationPending = useAppSelector((state: RootState) => selectMutationPending(state, { assetId: asset?.id }))
  const mutationError = useAppSelector((state: RootState) => selectMutationError(state, { assetId: asset?.id }))

  const initializeError = useAppSelector((state: RootState) => selectInitializeError(state, { matterId }))
  useEffect(() => {
    if (matterId) dispatch(TimelineAssetActions.setInitializeError({ matterId, error: null }))
  }, [dispatch, matterId])

  const assetId = asset?.id
  useEffect(() => {
    if (assetId) dispatch(TimelineAssetActions.setMutationError({ assetId, error: null }))
  }, [assetId, dispatch])

  const submitPending = initializePending || mutationPending

  const [filePickerTab, setFilePickerTab] = useState<AddFilesModalInitialTab | null>(null)
  const [previewItemsByFileId, setPreviewItemsByFileId] = useState<Record<string, FileAddPreviewItem>>({})
  const [isPreviewPending, setIsPreviewPending] = useState(false)
  const [previewError, setPreviewError] = useState<string | null>(null)

  const stagedUserFileIds = staging.stagedUserFileIds
  const { filesById: stagedUserFilesById, error: stagedUserFilesLoadError } = useUserFiles(stagedUserFileIds)

  const upload = useTimelineAssetFilesUpload({ matterId: matterId ?? null })

  const { canWrite, recordActivity, enabled: leaseEnabled, lease } = useTimelineEditingLease()
  const editingMatterRef = useRef<string | undefined>(undefined)
  useEffect(() => {
    if (canWrite) editingMatterRef.current = matterId
    else if (matterId && editingMatterRef.current === matterId && leaseEnabled && lease?.state === EditingLeaseState.AVAILABLE) {
      dispatch(TimelineAssetActions.setAddFilesLeaseExpiredMatterId(matterId))
      navigate(matterPaths.timeline.index(matterId), { replace: true })
    }
  }, [canWrite, matterId, leaseEnabled, lease?.state, dispatch, navigate])

  // Upload progress and processing polls for this page's staged files count as activity.
  useEffect(() => {
    if (!canWrite) return
    return dispatch(
      addTimelineAssetListener({
        matcher: isAnyOf(UserFilesPollingActions.pollBatchReceived, UppyFileUploadActions.uploadProgress),
        effect: (action, listenerApi) => {
          const state = listenerApi.getState()
          const staging = state.timelineAssetState.addFilesStaging
          if (staging.matterId !== matterId) return
          if (UppyFileUploadActions.uploadProgress.match(action)) {
            const { clientTmpId, progress } = action.payload
            const upload = state.uppyFileUploadState.files[clientTmpId]?.client
            if (upload?.uploadStatus !== 'uploading' || !upload.serverFileId || !staging.stagedUserFileIds.includes(upload.serverFileId)) return
            const previousProgress = listenerApi.getOriginalState().uppyFileUploadState.files[clientTmpId]?.client.uploadProgress ?? 0
            if (progress > previousProgress) recordActivity()
            return
          }
          if (!UserFilesPollingActions.pollBatchReceived.match(action)) return
          const stillProcessing = staging.stagedUserFileIds.some((fileId) => {
            const status = action.payload.files[fileId]?.record.processing_status
            return status === UserFileProcessingStatus.Pending || status === UserFileProcessingStatus.Processing
          })
          if (stillProcessing) recordActivity()
        },
      })
    )
  }, [canWrite, dispatch, matterId, recordActivity])

  // Reset staging when entering for a different matter
  useEffect(() => {
    if (matterId && staging.matterId !== matterId) {
      dispatch(TimelineAssetActions.resetAddFilesStaging({ matterId }))
    }
  }, [dispatch, matterId, staging.matterId])

  // Fingerprint identity changes when the staged set OR any staged file's processing status
  // changes, so the preview re-runs when an upload finishes processing (not just when the id
  // list changes) - otherwise a NOT_READY disposition goes stale and keeps blocking the CTA
  // after the file completes. Staged ids are derived from the fingerprint so the effect can
  // depend on a single stable value (user_file ids are UUIDs and carry no ':').
  const stagedReadinessFingerprint = stagedUserFileIds
    .map((fileId) => {
      const uploadState = uploadFilesByServerFileId[fileId]
      const file = uploadState?.backend?.record ?? stagedUserFilesById[fileId]
      return `${fileId}:${file?.processing_status ?? 'unknown'}`
    })
    .join(',')
  const stagedPreviewIds = useMemo(
    () => (stagedReadinessFingerprint ? stagedReadinessFingerprint.split(',').map((entry) => entry.split(':')[0]) : []),
    [stagedReadinessFingerprint]
  )

  // Debounced disposition preview against the existing timeline
  useEffect(() => {
    if (!asset || stagedPreviewIds.length === 0) {
      setPreviewItemsByFileId({})
      setIsPreviewPending(false)
      setPreviewError(null)
      return
    }
    let cancelled = false
    const assetId = asset.id
    setIsPreviewPending(true)
    setPreviewError(null)
    const timeout = setTimeout(() => {
      postPreviewAddFiles(assetId, { user_file_ids: stagedPreviewIds })
        .then((response) => {
          if (cancelled) return
          const byId: Record<string, FileAddPreviewItem> = {}
          for (const item of response.items) byId[item.user_file_id] = item
          setPreviewItemsByFileId(byId)
          setPreviewError(null)
        })
        .catch((error) => {
          if (cancelled) return
          console.error('Error previewing add files:', error)
          setPreviewItemsByFileId({})
          refreshMatterAfterWriteAccessError(error, asset.matter_id, dispatch)
          setPreviewError("Couldn't verify this file. Try attaching again.")
        })
        .finally(() => {
          if (!cancelled) setIsPreviewPending(false)
        })
    }, kPreviewDebounceMs)
    return () => {
      cancelled = true
      clearTimeout(timeout)
    }
  }, [asset, stagedPreviewIds, dispatch])

  // First-generation errors return to the timeline error banner; lease denials preserve staging.
  const wasPendingRef = useRef(false)
  const submittedRef = useRef<'initialize' | 'update' | null>(null)
  useEffect(() => {
    if (submittedRef.current && wasPendingRef.current && !submitPending && matterId) {
      const wasInitialization = submittedRef.current === 'initialize'
      const error = wasInitialization ? initializeError : mutationError
      submittedRef.current = null
      if (!error || (wasInitialization && typeof error === 'string')) navigate(matterPaths.timeline.index(matterId))
    }
    wasPendingRef.current = submitPending
  }, [submitPending, initializeError, mutationError, matterId, navigate])

  const stagedCards = stagedUserFileIds.map((fileId) => {
    const uploadState = uploadFilesByServerFileId[fileId]
    const file = uploadState?.backend?.record ?? stagedUserFilesById[fileId]
    const cardState = getTimelineAssetFileCardState({ fileId, file, uploadState, loadError: stagedUserFilesLoadError && !file })
    // With an existing asset the disposition comes from the server preview (diff vs the timeline).
    // On a first-time generation there's no asset to diff against, so preview-add never runs — every
    // ready file is implicitly NEW. Surface that locally so the "Ready to process" line still shows.
    const preview: FileAddPreviewItem | undefined = asset
      ? previewItemsByFileId[fileId]
      : cardState.ready
      ? { user_file_id: fileId, display_name: cardState.fileName, disposition: FileAddDisposition.NEW, possible_duplicate_of: null }
      : undefined
    return { fileId, preview, ...cardState }
  })
  const pendingCards = staging.pendingUploadClientIds.map((clientTmpId) => {
    const uploadState = uploadFilesByClientTmpId[clientTmpId]
    return { clientTmpId, ...getTimelineAssetFileCardState({ uploadState }) }
  })

  const hasBlockingDisposition = stagedCards.some((card) => card.preview && dispositionDisplay(card.preview)?.blocking)
  const hasUnreadyStaged = stagedCards.some((card) => !card.ready && !card.preview)
  const hasPendingUploads = pendingCards.length > 0
  const isAssetLoading = asset === undefined
  const hasPreviewPending = Boolean(asset && stagedCards.some((card) => card.ready && !card.preview))
  const totalStaged = stagedUserFileIds.length + pendingCards.length
  const readyStagedCount = stagedCards.filter((card) => card.ready).length
  const hasUnresolvedFailedFilesUpdate = Boolean(failedFilesUpdate)
  // Nothing to do if every staged file is already in the timeline — block the no-op submit.
  const allStagedAlreadyIncluded =
    stagedCards.length > 0 && !hasPendingUploads && stagedCards.every((card) => card.preview?.disposition === FileAddDisposition.ALREADY_INCLUDED)

  const submitDisabled =
    stagedUserFileIds.length === 0 ||
    isAssetLoading ||
    submitPending ||
    isUpdateActive ||
    hasUnresolvedFailedFilesUpdate ||
    isPreviewPending ||
    hasPreviewPending ||
    Boolean(previewError) ||
    hasBlockingDisposition ||
    hasUnreadyStaged ||
    hasPendingUploads ||
    allStagedAlreadyIncluded

  const handleSubmit = useCallback(() => {
    if (submitDisabled || !matterId) return
    submittedRef.current = asset ? 'update' : 'initialize'
    if (asset) {
      trackTimelineEvent(AnalyticsEvent.TimelineAssetUpdateClicked, { timelineAssetId: asset.id, timelineSelectedFilesCount: stagedUserFileIds.length })
      dispatch(TimelineAssetActions.applyUpdate({ assetId: asset.id, addUserFileIds: stagedUserFileIds }))
    } else {
      trackTimelineEvent(AnalyticsEvent.TimelineAssetGenerateClicked, { matterId, timelineSelectedFilesCount: stagedUserFileIds.length })
      dispatch(TimelineAssetActions.initializeTimeline({ matterId, userFileIds: stagedUserFileIds }))
    }
  }, [submitDisabled, matterId, asset, dispatch, stagedUserFileIds])

  const handleClearAll = useCallback(() => {
    staging.pendingUploadClientIds.forEach((clientTmpId) => upload.cancelUpload(clientTmpId))
    if (matterId) dispatch(TimelineAssetActions.resetAddFilesStaging({ matterId }))
  }, [staging.pendingUploadClientIds, upload, matterId, dispatch])

  return (
    <div className="grow flex flex-col w-full h-full overflow-hidden">
      <MatterBreadcrumbsTimelineAsset showAddFilesCrumb />

      <FileStagingPage
        matterId={matterId}
        upload={upload}
        title="Add Files"
        description="Upload or select an entire folder of case files, no splitting or batching required."
        totalStaged={totalStaged}
        readyStagedCount={readyStagedCount}
        onOpenFilePicker={setFilePickerTab}
        onClearAll={handleClearAll}
        onSubmit={handleSubmit}
        submitDisabled={submitDisabled || upload.preparing}
        submitPending={submitPending}
        submitLabel={asset ? 'Update Timeline' : 'Generate Timeline'}
        pendingLabel={asset ? 'Updating…' : 'Generating…'}
        beforeFiles={
          asset && asset.total_files > 0 ? (
            <div className="shrink-0">
              <IncludedInTimelineDrawer assetId={asset.id} totalFiles={asset.total_files} />
            </div>
          ) : undefined
        }
      >
        {stagedCards.map((card) => (
          <MatterRecordStagedFileCard
            key={card.fileId}
            fileName={card.fileName}
            inProgressLabel={inProgressLabel(card.status)}
            disposition={dispositionDisplay(card.preview)}
            duplicateOf={card.preview?.possible_duplicate_of?.display_name}
            errorMessage={card.errorMessage || previewError || undefined}
            onRemove={() => dispatch(TimelineAssetActions.unstageUserFileId({ fileId: card.fileId }))}
          />
        ))}
        {pendingCards.map((card) => (
          <TimelineFileCard
            key={card.clientTmpId}
            fileName={card.fileName}
            status={card.status}
            errorMessage={card.errorMessage || undefined}
            onRemove={() => {
              upload.cancelUpload(card.clientTmpId)
              dispatch(TimelineAssetActions.removePendingUserFileUpload(card.clientTmpId))
            }}
          />
        ))}
      </FileStagingPage>

      <AddFilesMatterFilesDialog
        open={filePickerTab !== null}
        matterId={matterId ?? null}
        initialSelectedIds={stagedUserFileIds}
        initialTab={filePickerTab ?? ADD_FILES_MODAL_TAB.MATTER_FILES}
        onClose={() => setFilePickerTab(null)}
        onApply={(userFileIds) => dispatch(TimelineAssetActions.setStagedUserFileIds({ fileIds: userFileIds }))}
      />
    </div>
  )
}
